import { Component } from '@theme/component';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Reveals its contents once when scrolled into view.
 *
 * Horizon ships no generic reveal, so this is the shared primitive. Every route
 * gets its character from data attributes alone — there are no per-route
 * branches in here.
 *
 * The from-state lives in sany-reveal.css and is gated on `.sany-motion`, a
 * class set synchronously in the document head. That way a visitor without JS,
 * or with reduced motion, is never served hidden content, and there is no frame
 * where content paints visible and then jumps to an offset.
 */

/** @type {Set<SanyReveal>} */
const instances = new Set();

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * Above 990px the page scrolls inside `.page-wrapper`, not the document, so an
 * observer with the default root never fires. The root also changes at that
 * breakpoint, and it is captured when the observer is built — so the observer
 * has to be rebuilt when the breakpoint flips, or every reveal below the fold
 * dies silently on resize.
 */
function buildObserver() {
  observer?.disconnect();

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const element = /** @type {SanyReveal} */ (entry.target);
        const threshold = Number(element.dataset.revealThreshold ?? 0.15);

        if (entry.intersectionRatio >= threshold) {
          element.reveal();
          if (element.dataset.revealRepeat !== 'true') observer?.unobserve(element);
        } else if (element.dataset.revealRepeat === 'true') {
          element.conceal();
        }
      }
    },
    {
      root: getIntersectionRoot(),
      // A shared set of thresholds; each element compares its own in the callback.
      threshold: [0, 0.15, 0.3],
      rootMargin: '0px 0px -8% 0px',
    }
  );

  for (const instance of instances) observer.observe(instance);
}

scrollContainerMediaQuery.addEventListener('change', () => {
  if (instances.size) buildObserver();
});

/**
 * Data attribute on the host -> CSS custom property the stylesheet reads.
 * Anything not declared keeps the default from sany-reveal.css.
 */
const TUNING = {
  revealDistance: '--sany-reveal-distance',
  revealDuration: '--sany-reveal-duration',
  revealDelay: '--sany-reveal-delay',
  revealStagger: '--sany-reveal-stagger',
  revealEasing: '--sany-reveal-easing',
};

/** @extends {Component<{ item?: HTMLElement[] }>} */
class SanyReveal extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#applyTuning();
    this.#applyStagger();

    instances.add(this);
    // Built lazily: `.page-wrapper` must exist, and section hydration re-runs us.
    if (!observer) buildObserver();
    else observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    instances.delete(this);
    observer?.unobserve(this);
  }

  /** Copies the declared timing onto the properties the stylesheet reads. */
  #applyTuning() {
    for (const [attribute, property] of Object.entries(TUNING)) {
      const value = this.dataset[attribute];
      if (value) this.style.setProperty(property, value);
    }
  }

  /**
   * Children marked `ref="item[]"` come in one after another. Flagging the host
   * keeps the CSS unambiguous: either the direct children animate as one, or the
   * marked items animate in sequence, never both.
   */
  #applyStagger() {
    const items = this.refs.item;
    if (!items?.length) return;

    this.classList.add('sany-reveal--staggered');
    items.forEach((element, index) => {
      element.classList.add('sany-reveal__item');
      element.style.setProperty('--sany-reveal-index', String(index));
    });
  }

  reveal() {
    this.classList.add('sany-reveal-visible');
  }

  conceal() {
    this.classList.remove('sany-reveal-visible');
  }
}

if (!customElements.get('sany-reveal')) {
  customElements.define('sany-reveal', SanyReveal);
}
